
import { Observable } from "rxjs";
import { map, share } from "rxjs/operators";
import { IHttpRequestConfig } from "../http";
import { HttpProvider } from "../http/http.provider";
import { IAppConfig } from "../http/types/IApplicationConfig";
import { addProvider, IProviderDescriptor, ServiceEndpointConstants } from "../ServiceUtil";
import
{
    IAccountManager,
    IHrManager,
    IManager,
    INewAccountManager,
    ISalesRepData,
    ISupervisor,
    IInterviewRep,
    IPayrolRecod,
    IAccountManagerResponse
} from "./account.manager.interface";

export class AccountManagerDelegate
{
    constructor(private SERVICE_CONFIG: IAppConfig, private http: HttpProvider)
    {
    }

    public getAllAccManagerById(accountManagerId: string): Observable<IAccountManagerResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                accountManagerId: accountManagerId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ACCOUNTS.GET_BY_ID, null, config).pipe(
            map((response: any) =>
            {
                return {
                    accountManager: this.normalizeAccountManager(response.data.accountManager),
                    accountManagerId: accountManagerId,
                    success: response.data.success
                } as IAccountManagerResponse;
            }),
            share());
    }

    public getAllAccManagerByOrgId(orgId: string): Observable<IManager>
    {
        const config: IHttpRequestConfig = {
            params: {
                orgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ACCOUNTS.GET, null, config).pipe(
            map((response: any) =>
            {
                return {
                    accountManagers: this.normalizeAccountManagers(response.data.accountManagers),
                    supervisors: this.normalizeSupervisors(response.data.supervisors),
                    hrManagers: this.normalizeHrManagers(response.data.hrManagers),
                    salesReps: this.normalizeSalesReps(response.data.salesReps),
                    leadReps: this.normalizeSalesReps(response.data.leadReps),
                    helpDeskOwners: response.data.helpDeskOwners,
                    interviewReps: this.normalizeInterviewReps(response.data.interviewReps),
                    payrollDates: this.normalizePayrollRecords(response.data.payrollRecords),
                    insuranceProvider: response.data.insuranceProvider,
                    healthInsurancePlanData: response.data.insuranceProvider ? response.data.insuranceProvider.healthInsurancePlanData : [],
                    dentalInsurancePlanData: response.data.insuranceProvider ? response.data.insuranceProvider.dentalInsurancePlanData : [],
                    visionInsurancePlanData: response.data.insuranceProvider ? response.data.insuranceProvider.visionInsurancePlanData : []
                } as IManager;
            }),
            share());
    }

    public createNewManager(newManager: INewAccountManager): Observable<IManager>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.ACCOUNTS.CREATE_ACCOUNT, newManager).pipe(
            map((response: any) =>
            {
                return {
                    accountManagerId: response.data.accountManagerId,
                    accountManagers: this.normalizeAccountManagers(response.data.accountManagers ? response.data.accountManagers : [])
                } as IManager;
            }),
            share());
    }

    public updateManager(manager: IAccountManager): Observable<IManager>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.ACCOUNTS.UPDATE_ACCOUNT, manager).pipe(
            map((response: any) =>
            {
                return {
                    accountManagers: this.normalizeAccountManagers(response.data.accountManagers ? response.data.accountManagers : [])
                } as IManager;
            }),
            share());
    }

    private normalizeAccountManager(manager: any): IAccountManager
    {

        return {
            accountManagerId: manager.accountManagerId,
            managerName: manager.managerName,
            orgId: manager.orgId,
            createLogin: !!manager.userId,
            active: manager.orgId,
            firstName: manager.firstName,
            middleName: manager.middleName,
            lastName: manager.lastName,
            fullName: manager.fullName,
            mobileNumber: manager.mobileNumber,
            alternateMobileNumber: manager.alternateMobileNumber,
            employee: manager.employee
        } as unknown as IAccountManager;

    }

    private normalizeAccountManagers(managers: any): IAccountManager[]
    {
        return managers.map(manager =>
        {
            return {
                accountManagerId: manager.accountManagerId,
                managerName: manager.managerName,
                orgId: manager.orgId,
                createLogin: !!manager.userId,
                active: manager.orgId,
                firstName: manager.firstName,
                middleName: manager.middleName,
                lastName: manager.lastName,
                fullName: manager.fullName,
                mobileNumber: manager.mobileNumber,
                alternateMobileNumber: manager.alternateMobileNumber
            } as IAccountManager;
        });
    }

    private normalizeSupervisors(managers: any): ISupervisor[]
    {
        return managers.map(manager =>
        {
            return {
                employeeSupervisorId: manager.employeeSupervisorId,
                supervisorName: manager.supervisorName,
                orgId: manager.orgId
            } as ISupervisor;
        });
    }


    private normalizeHrManagers(managers: any): IHrManager[]
    {
        return managers.map(manager =>
        {
            return {
                hrManagerId: manager.hrManagerId,
                hrManagerName: manager.hrManagerName,
                orgId: manager.orgId
            } as IHrManager;
        });
    }

    private normalizeSalesReps(salesReps: any): ISalesRepData[]
    {
        return salesReps.map(salesRep =>
        {
            return {
                salesRepId: salesRep.salesRepId,
                salesRepFullName: salesRep.salesRepFullName
            } as ISalesRepData;
        });
    }

    private normalizeInterviewReps(interviewReps: any): IInterviewRep[]
    {
        return interviewReps.map(interviewRep =>
        {
            return {
                interviewRepId: interviewRep.interviewRepId,
                interviewRepName: interviewRep.interviewRepName,
                displayName: interviewRep.displayName
            } as IInterviewRep;
        });
    }

    private normalizePayrollRecords(payrolLRecords: any): IPayrolRecod[]
    {
        return payrolLRecords.map(payrollRecord =>
        {
            return {
                id: payrollRecord.employeePayrollRecordId,
                payrollDate: payrollRecord.payrollDate,
                orgId: payrollRecord.orgId,
                payDateToString: payrollRecord.payDateToString
            } as IPayrolRecod;
        });
    }
}